import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Tab, 
  Tabs, 
  CircularProgress,
  Alert
} from '@mui/material';
import { PersonOutline, LockOutlined, PhotoCameraOutlined, SettingsOutlined } from '@mui/icons-material';
import { User } from '../../types/user';
import { profileService } from '../../services/profileService';
import { AvatarUpload } from '../../components/profile/AvatarUpload';
import { PasswordChangeForm } from '../../components/profile/PasswordChangeForm';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ mt: 1.875 }}>{children}</Box>}
    </div>
  );
}

export const ProfilePage: React.FC = () => {
  const { user: contextUser, isAuthenticated, updateUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && contextUser) {
      loadUserProfile();
    }
  }, [contextUser, isAuthenticated]);

  const loadUserProfile = async () => {
    if (!contextUser) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const profile = await profileService.getCurrentProfile();
      setUser(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải thông tin profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleUserUpdate = (updatedUser: User) => {
    setUser(updatedUser);
    // Update AuthContext to reflect changes in navbar
    updateUser(updatedUser);
  };

  const handlePasswordChanged = () => {
    // Force user to re-login after password change
    setTimeout(() => {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }, 2000);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Không tìm thấy thông tin người dùng</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Quản lý tài khoản
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<PersonOutline />} label="Thông tin cá nhân" />
          <Tab icon={<PhotoCameraOutlined />} label="Ảnh đại diện" />
          <Tab icon={<LockOutlined />} label="Đổi mật khẩu" />
          <Tab icon={<SettingsOutlined />} label="Cài đặt" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box>
          <Box sx={{ 
            p: 3, 
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
          }}>
            <Typography variant="h6" gutterBottom>
              Thông tin cá nhân
            </Typography>
            <Typography color="text.secondary">
              Tính năng cập nhật thông tin cá nhân sẽ được hoàn thiện trong phiên bản tiếp theo.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography><strong>Tên:</strong> {user.name}</Typography>
              <Typography><strong>Email:</strong> {user.email}</Typography>
              {user.phone && <Typography><strong>Điện thoại:</strong> {user.phone}</Typography>}
              {user.bio && <Typography><strong>Mô tả:</strong> {user.bio}</Typography>}
            </Box>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box>
          <AvatarUpload user={user} onAvatarUpdated={handleUserUpdate} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box>
          <PasswordChangeForm onPasswordChanged={handlePasswordChanged} />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box>
          <Box sx={{ 
            p: 3, 
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
          }}>
            <Typography variant="h6" gutterBottom>
              Cài đặt ứng dụng
            </Typography>
            <Typography color="text.secondary">
              Tính năng cài đặt preferences sẽ được hoàn thiện trong phiên bản tiếp theo.
            </Typography>
          </Box>
        </Box>
      </TabPanel>
    </Container>
  );
}; 